/**
 * Zurück an die alte Stelle (Rückmeldungen 1.1).
 *
 * Überall gibt es einen Zurück-Knopf. Er führt in die vorherige Ansicht, mit
 * demselben Zustand und derselben Scrollposition. Dafür steht, was eine
 * Ansicht ausmacht -- Suchbegriff, aufgeklappte Knoten, Filter -- in der
 * Adresse, und die Scrollposition merkt sich der Browser-Tab je
 * Verlaufseintrag.
 */

import { useEffect } from "react";
import {
  NavigationType,
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from "react-router";

/** Ein Text in der Adresse, etwa der Suchbegriff. Ändern ersetzt den Eintrag. */
export function useAdressText(name: string): [string, (neu: string) => void] {
  const [parameter, setParameter] = useSearchParams();
  const setzen = (neu: string): void => {
    setParameter(
      (alt) => {
        const p = new URLSearchParams(alt);
        if (neu === "") p.delete(name);
        else p.set(name, neu);
        return p;
      },
      { replace: true },
    );
  };
  return [parameter.get(name) ?? "", setzen];
}

/** Eine Menge von Kennungen in der Adresse, etwa aufgeklappte Knoten. */
export function useAdressMenge(
  name: string,
): [ReadonlySet<string>, (neu: ReadonlySet<string>) => void] {
  const [text, setText] = useAdressText(name);
  const menge = new Set(text === "" ? [] : text.split(","));
  return [
    menge,
    (neu) => {
      setText([...neu].join(","));
    },
  ];
}

function scrollSchluessel(key: string): string {
  return `thw-scroll-${key}`;
}

/**
 * Stellt beim Zurück die Scrollposition wieder her; eine neue Ansicht beginnt
 * oben. Der Inhalt kommt aus IndexedDB und steht nicht sofort da -- deshalb
 * ein paar Versuche, bis die Seite lang genug ist.
 */
export function ScrollZurueck() {
  const ort = useLocation();
  const art = useNavigationType();

  useEffect(() => {
    const schluessel = scrollSchluessel(ort.key);
    let uhr: number | undefined;
    if (art === NavigationType.Pop) {
      let ziel = 0;
      try {
        ziel = Number(globalThis.sessionStorage.getItem(schluessel) ?? "0");
      } catch {
        // Ohne Speicher eben oben.
      }
      let versuche = 0;
      const versuch = (): void => {
        globalThis.scrollTo(0, ziel);
        versuche += 1;
        if (Math.abs(globalThis.scrollY - ziel) > 2 && versuche < 30) {
          uhr = window.setTimeout(versuch, 50);
        }
      };
      uhr = window.setTimeout(versuch, 0);
    } else if (art === NavigationType.Push) {
      globalThis.scrollTo(0, 0);
    }

    const merken = (): void => {
      try {
        globalThis.sessionStorage.setItem(schluessel, String(Math.round(globalThis.scrollY)));
      } catch {
        // Ohne Speicher kein Zurück an die alte Stelle -- sonst geht alles.
      }
    };
    globalThis.addEventListener("scroll", merken, { passive: true });
    return () => {
      globalThis.removeEventListener("scroll", merken);
      if (uhr !== undefined) window.clearTimeout(uhr);
    };
  }, [ort.key, art]);

  return null;
}

/**
 * Der Zurück-Knopf in der Kopfleiste. Kam man von einer anderen Ansicht der
 * App, geht es dorthin zurück; wurde die Seite direkt geöffnet, etwa über
 * ein Etikett, zur Suche.
 */
export function ZurueckKnopf() {
  const ort = useLocation();
  const gehZu = useNavigate();
  if (ort.pathname === "/") return null;
  return (
    <button
      type="button"
      aria-label="Zurück"
      onClick={() => {
        if (ort.key === "default") void gehZu("/", { replace: true });
        else void gehZu(-1);
      }}
      className="flex min-h-12 min-w-12 shrink-0 items-center justify-center rounded-lg text-2xl font-bold active:bg-blue-900"
    >
      ‹
    </button>
  );
}
